import * as helios from "@hyperionbt/helios";
import * as Sentry from "@sentry/nextjs";
import { excludeNullishElements } from "common-utils/utils/array";
import { Artwork } from "kreate-common/modules/business-types";
import { MetadataEnvOffChain } from "kreate-common/modules/metadata-env/types";
import { parseKreateUtxoProtocolParams } from "kreate-common/modules/utxo-parser/by-type/protocol-params";
import { CLIENT_ENV } from "kreate-env/client";
import { buildPartialTx$MintArtwork } from "kreate-protocol/offchain/tx/mintArtwork";
import { httpGet$GetArtworkInlinableFiles } from "kreate-server/commands/GetArtworkInlinableFiles/fetcher";
import { httpGet$GetProtocolParams } from "kreate-server/commands/GetProtocolParams/fetcher";
import { MdFavorite } from "react-icons/md";

import { assert } from "@/modules/common-utils/assert";
import { waitForUtxo } from "@/modules/common-utils/async";
import { formatLovelaceAmount$Standard } from "@/modules/common-utils/format";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import {
  getAssetFingerprintFromParts,
  getPoolPmUrl,
} from "@/modules/kreate-components/components/LinkPoolPm/utils";
import { waitModal } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import ModalProgressV2 from "@/modules/kreate-modals/modals/ModalProgressV2";
import { anticipateSteps } from "@/modules/kreate-modals/modals/ModalProgressV2/utils";
import ModalSuccess from "@/modules/kreate-modals/modals/ModalSuccess";
import ModalTransactionBreakdown from "@/modules/kreate-modals/modals/ModalTransactionBreakdown";

// NOTE: the stats are calculated by taking the medians of the
// last 10 samples before Fri 18, 14:20
const steps = anticipateSteps({
  buildingTransaction: 5484,
  waitingForTransactionBreakdown: 2166,
  waitingForSignatureAndSubmission: 8213,
  waitingForConfirmation: 23238,
});

type Params = {
  artwork: Artwork;
  cip30Wallet: helios.Cip30Wallet;
  numMinting: number;
};

export async function* runFlow$MintArtwork({
  artwork,
  cip30Wallet,
  numMinting,
}: Params) {
  assert(artwork.utxo?.datum.type === "artwork", "not eligible for minting");

  addBreadcrumb("building transaction...");
  yield (
    <ModalProgressV2
      open
      content="Building transaction..."
      ratio={steps.buildingTransaction.ratio}
      expectedDuration={steps.buildingTransaction.duration}
    />
  );

  const [protocolParamsUtxo, inlinableFiles] = await Promise.all([
    httpGet$GetProtocolParams("/api/v1/utxo/protocol-params"),
    httpGet$GetArtworkInlinableFiles(
      `/api/v1/artwork/${artwork.artworkId}/inlinable-files`
    ),
  ]);

  const { datumProtocolParams } =
    parseKreateUtxoProtocolParams(protocolParamsUtxo);

  const { tx, assetIdParts, protocolFee, mintFee, txFee, isOwner } =
    await buildPartialTx$MintArtwork({
      network: CLIENT_ENV.CARDANO_NETWORK,
      wallet: cip30Wallet,
      utxoProtocolParams: protocolParamsUtxo,
      utxoArtwork: artwork.utxo,
      artwork: artwork,
      mintNumber: numMinting,
      txMessage: `『${artwork.title}』${
        artwork.populated?.createdBy?.displayName
          ? ` by ${artwork.populated.createdBy.displayName}`
          : ""
      } was just minted!`,
      metadataEnvOffChain: MetadataEnvOffChain.parse({
        artist: artwork.populated?.createdBy,
        collection: artwork.populated?.collection,
      } satisfies MetadataEnvOffChain),
      fileIdToText: inlinableFiles.fileIdToText,
    });

  addBreadcrumb("waiting for transaction breakdown...");
  yield* waitModal<void>((resolve, reject) => (
    <ModalTransactionBreakdown
      open
      imageFile={artwork.imageFile || artwork.file}
      onAccept={resolve}
      onReject={() => reject(null)}
      rows={excludeNullishElements([
        mintFee
          ? {
              label: "Artwork Price",
              lovelaceAmount: mintFee,
              tooltip: "Artwork price",
            }
          : undefined,
        !protocolFee
          ? undefined
          : isOwner
          ? {
              label: "Platform Fee",
              lovelaceAmount: protocolFee,
              tooltip: `${formatLovelaceAmount$Standard(
                datumProtocolParams.kreatorMintFee
              )} × No. Editions, minimum ${formatLovelaceAmount$Standard(
                CLIENT_ENV.MIN_PROTOCOL_FEE
              )}`,
            }
          : {
              label: "Platform Fee",
              lovelaceAmount: protocolFee,
              tooltip: `${
                CLIENT_ENV.KOLLECTOR_MINT_FEE_PERCENTAGE
              }% of Artwork Price, minimum ${formatLovelaceAmount$Standard(
                CLIENT_ENV.MIN_PROTOCOL_FEE
              )}`,
            },
        {
          label: "Transaction Fee",
          lovelaceAmount: txFee,
          tooltip: "The blockchain network fees to process this transaction",
        },
      ])}
    />
  ));

  addBreadcrumb("waiting for signature and submission...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for signature and submission..."
      ratio={steps.waitingForSignatureAndSubmission.ratio}
      expectedDuration={steps.waitingForSignatureAndSubmission.duration}
    />
  );

  const signatures = await cip30Wallet.signTx(tx);
  tx.addSignatures(signatures);

  const txId = await cip30Wallet
    .submitTx(tx)
    .then((txId$Helios) => txId$Helios.hex);

  addBreadcrumb("waiting for confirmation...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for confirmation..."
      ratio={steps.waitingForConfirmation.ratio}
      expectedDuration={steps.waitingForConfirmation.duration}
    />
  );

  await waitForUtxo(cip30Wallet, (utxo) => utxo.txId.hex === txId);
  await shamelesslyRevalidateEverything();

  addBreadcrumb("showing success message.");
  yield* waitModal<void>((resolve) => (
    <ModalSuccess
      open
      icon={<MdFavorite />}
      content={`You have minted ${numMinting} Editions.`}
      onClose={resolve}
      buttons={[
        {
          isPrimary: true,
          content: "View on Pool.pm",
          onClick: () => {
            const assetFingerprint = getAssetFingerprintFromParts(assetIdParts);
            const poolPmUrl = getPoolPmUrl(assetFingerprint);
            window.open(poolPmUrl, "_blank", "noreferrer");
          },
        },
        {
          content: "Close",
          onClick: "close",
        },
      ]}
    />
  ));

  Sentry.captureEvent({ message: "runFlow$MintArtwork", level: "info" });
}

const addBreadcrumb = (msg: string) => {
  const terms = ["runFlow$MintArtwork", Date.now(), encodeURIComponent(msg)];
  Sentry.addBreadcrumb({ message: terms.join(" - ") });
};
